import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/react-hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import accountIcon from "@iconify/icons-mdi/account";
import keyIcon from "@iconify/icons-mdi/key";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import { SSO_LOGIN } from "my-hr/graphql";
import { OcLoading } from "oc/components";
import { useOcClientState } from "oc/hooks";
import useOcProperty from "oc/hooks/use-property";

const validator = new Validator();

type MyHrUnlockSSOFormProps = {
  onSubmit: (loginResult: any) => void;
  buttonLabel: string;
  username: string;
};

export const MyHrUnlockSSOForm: React.FC<MyHrUnlockSSOFormProps> = ({
  onSubmit,
  buttonLabel,
  username,
}) => {
  const { t } = useTranslation();

  const schema = {
    username: {
      type: "string",
      min: 1,
      messages: {
        string: "rossz azonosító",
        stringMin: t("MY_HR_ERROR_MESSAGE_USERNAME_IS_REQUIRED"),
      },
    },
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "Nem megfelelő jelszó!",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [password, setPassword] = useOcClientState<string>("loginPassword", "");
  const [showPassword, setShowPassword] = useState(false);

  const [passwordError, setPasswordError] = useOcClientState<boolean>(
    "loginPasswordError",
    false,
  );
  const [passwordErrorText, setPasswordErrorText] = useOcClientState(
    "loginPasswordErrorText",
    "",
  );

  const { data: passwordVisibilityData } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const [ssoLogin, { loading, data: ssoLoginData, error: ssoLoginError }] = useLazyQuery(SSO_LOGIN, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (ssoLoginData?.ssoLogin) {
      onSubmit(ssoLoginData.ssoLogin);
    }
  }, [ssoLoginData, onSubmit]);

  // Timeout: if loading takes more than 10 seconds, reload page
  // This handles hibernation/sleep cases where network is stuck
  useEffect(() => {
    if (loading) {
      const timeoutId = setTimeout(() => {
        window.location.reload();
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  useEffect(() => {
    if (ssoLoginError?.graphQLErrors) {
      ssoLoginError.graphQLErrors.forEach((item) => {
        if (item.message === "Wrong password!") {
          setPasswordErrorText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
          setPasswordError(true);
        }
      });
    }
  }, [ssoLoginError, t, setPasswordErrorText, setPasswordError]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isValidated = check({ username, password });

    // Handle both sync and async validation
    if (typeof (isValidated as Promise<any>).then === "function") {
      isValidated = await isValidated;
    }

    if (isValidated !== true) {
      (isValidated as any[]).forEach((item) => {
        if (item.field === "password") {
          setPasswordErrorText(item.message);
          setPasswordError(true);
        }
      });
      return;
    }

    ssoLogin({ variables: { username, password } });
  };

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  useEffect(() => {
    const handleClear = () => {
      setPassword("");
      setPasswordError(false);
      setPasswordErrorText("");
    };
    return () => {
      handleClear();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <OcLoading show={loading} />
      <form noValidate autoComplete="off" onSubmit={handleLogin}>
        <Box paddingTop="18px">
          <TextField
            id="username"
            value={username}
            label={t("MY_HR_USERNAME")}
            variant="outlined"
            fullWidth
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InlineIcon
                    height="24px"
                    icon={accountIcon}
                    color="#919699"
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box height="32px" />
        <Box>
          <TextField
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label={t("MY_HR_PASSWORD")}
            variant="outlined"
            type={showPassword ? "text" : "password"}
            fullWidth
            helperText={passwordErrorText}
            error={passwordError}
            onFocus={() => {
              setPasswordErrorText("");
              setPasswordError(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InlineIcon height="24px" icon={keyIcon} color="#919699" />
                  <Box width="8px" />
                  {passwordVisibilityData === "true" && (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      style={{
                        backgroundColor: "#F1F3F4",
                        height: "48px",
                        width: "48px",
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon
                          style={{ height: "24px", width: "24px" }}
                        />
                      ) : (
                        <VisibilityIcon
                          style={{ height: "24px", width: "24px" }}
                        />
                      )}
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box height="32px" />
        <Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
          >
            {buttonLabel}
          </Button>
        </Box>
      </form>
    </Fragment>
  );
};

export default MyHrUnlockSSOForm;
